import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../shared/lib/supabaseClient';
import { ManualTransactionFormData } from '../components/modals/ManualTransactionForm';
import { useAuth } from '../../auth/context/AuthContext';

/**
 * useAccountTransactions
 * 
 * React Query hook for fetching transactions for a specific account.
 * Transactions are stored separately from accounts in React Query cache,
 * eliminating race conditions when dashboard data refetches.
 * 
 * @param accountId - The account ID to fetch transactions for
 * @returns React Query result with transactions data
 */
export function useAccountTransactions(accountId?: string) {
  const { user } = useAuth();

  return useQuery({
    enabled: !!accountId && !!user?.id,
    queryKey: ['transactions', accountId],
    queryFn: async (): Promise<ManualTransactionFormData[]> => {
      if (!accountId || !user?.id) {
        throw new Error('Account ID and user ID are required');
      }

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('account_id', accountId)
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      return (data ?? []).map((dbTx: any): ManualTransactionFormData => ({
        id: dbTx.id,
        type: dbTx.type,
        date: dbTx.date,
        ticker: dbTx.ticker,
        quantity: dbTx.quantity,
        price_per_unit: dbTx.price_per_unit,
        commission: dbTx.commission,
        contracts: dbTx.contracts,
        strike_price: dbTx.strike_price,
        expiration_date: dbTx.expiration_date,
        total_amount: dbTx.total_amount,
        notes: dbTx.notes,
        isHidden: dbTx.is_hidden,
        is_manually_edited: dbTx.is_manually_edited,
        data_quality_status: dbTx.data_quality_status,
        data_quality_reason: dbTx.data_quality_reason,
        brokerage_transaction_id: dbTx.brokerage_transaction_id,
        brokerage_description: dbTx.brokerage_description,
        brokerage_type: dbTx.brokerage_type,
        brokerage_quantity: dbTx.brokerage_quantity,
      }));
    },
    // Keep data fresh for 30 seconds to avoid unnecessary refetches
    staleTime: 30_000,
  });
}
